import {ReactElement, useEffect, useState} from "react";
import {Field, Input, Label, Message} from "@zendeskgarden/react-forms";
import {Button} from "@zendeskgarden/react-buttons";
import {Col, Row} from "@zendeskgarden/react-grid";
import {CustomZDDropdown} from "../../../../../atoms/custom-zd-dropdown/CustomZDDropdown";
import {
    AllPayrollCompensationType,
    AllPayrollRaiseFrequencyAndType,
    AllPayrollBonusFrequencyAndType,
    AllMonths,
    AllFebDates,
    All30Dates,
    All31Dates,
} from "../../../../../components/analyst-payroll-drawer/helpers/types";
import {
    PayrollBonusFrequency,
    PayrollBonusType,
    PayrollCompensationType,
    PayrollRaiseFrequency,
    PayrollRaiseType,
} from "../../../../../__generated__/generated_types";
import styles from "../PayrollDefaults.module.scss";
import {
    LocalBaseDefault,
    LocalRaiseDefault,
    LocalBonusDefault,
    LocalOvertimeDefault,
} from "../DefaultsForm";
import PayrollDatePicker from "../../../../../components/analyst-payroll-drawer/components/PayrollDatePicker";
import {ensureDateInUtc} from "../../../../../components/analyst-payroll-drawer/date-helpers";
import {v4 as uuid} from "uuid";

interface DefaultsEarningsProps {
    bases: LocalBaseDefault[];
    raises: LocalRaiseDefault[];
    bonuses: LocalBonusDefault[];
    overtimes: LocalOvertimeDefault[];
    setBases: (bases: LocalBaseDefault[]) => void;
    setRaises: (raises: LocalRaiseDefault[]) => void;
    setBonuses: (bonuses: LocalBonusDefault[]) => void;
    setOvertimes: (overtimes: LocalOvertimeDefault[]) => void;
    onResetBases: () => void;
    onResetRaises: () => void;
    onResetBonuses: () => void;
    onResetOvertimes: () => void;
    basesChanged: boolean;
    raisesChanged: boolean;
    bonusesChanged: boolean;
    overtimesChanged: boolean;
    onValidationChange?: (hasErrors: boolean) => void;
    showResetControls?: boolean;
}

type ValidationErrors = Record<string, string>;
type TouchedFields = Record<string, boolean>;

function getDatesForMonth(month: string) {
    if(["1", "01"].indexOf(month) !== -1) {
        return AllFebDates;
    } else if(["3", "03", "5", "05", "8", "08", "10"].indexOf(month) !== -1) {
        return All30Dates;
    }
    return All31Dates;
}

function hasValue(value: string | undefined | null): boolean {
    return (value ?? "").trim().length > 0;
}

export default function DefaultsEarnings(props: DefaultsEarningsProps): ReactElement {
    const {
        bases,
        raises,
        bonuses,
        overtimes,
        setBases,
        setRaises,
        setBonuses,
        setOvertimes,
        onResetBases,
        onResetRaises,
        onResetBonuses,
        onResetOvertimes,
        basesChanged,
        raisesChanged,
        bonusesChanged,
        overtimesChanged,
        onValidationChange,
        showResetControls = true,
    } = props;
    const base = bases[0];
    const [errors, setErrors] = useState<ValidationErrors>({});
    const [touchedFields, setTouchedFields] = useState<TouchedFields>({});

    function markTouched(fieldKey: string) {
        setTouchedFields(current => ({...current, [fieldKey]: true}));
    }

    function fieldError(fieldKey: string): string | undefined {
        return touchedFields[fieldKey] ? errors[fieldKey] : undefined;
    }

    function validate(): ValidationErrors {
        const nextErrors: ValidationErrors = {};

        if(base) {
            if(!base.compensationType) {
                nextErrors["base.compensationType"] = "Compensation Type is required";
            } else if(base.compensationType === PayrollCompensationType.Annual && !hasValue(base.annualCompensation)) {
                nextErrors["base.annualCompensation"] = "Annual Amount is required";
            } else if(base.compensationType === PayrollCompensationType.Hourly) {
                if(!hasValue(base.hourlyCompensation)) nextErrors["base.hourlyCompensation"] = "Hourly Rate is required";
                if(!hasValue(base.expectedHoursPerWeek)) nextErrors["base.expectedHoursPerWeek"] = "Hours/Week is required";
            }
        }

        raises.forEach((raise, index) => {
            if(!raise.frequency || !raise.raiseType) {
                nextErrors[`raise.${index}.type`] = "Type is required";
                return;
            }

            if(!hasValue(raise.amount)) nextErrors[`raise.${index}.amount`] = "Amount is required";
            if(raise.frequency === PayrollRaiseFrequency.Annually) {
                if(!hasValue(raise.raiseMonth)) nextErrors[`raise.${index}.month`] = "Month is required";
                if(!hasValue(raise.raiseDay)) nextErrors[`raise.${index}.day`] = "Day is required";
            } else if(raise.frequency === PayrollRaiseFrequency.Once && !raise.onetimeRaiseDate) {
                nextErrors[`raise.${index}.date`] = "Effective Date is required";
            }
        });

        bonuses.forEach((bonus, index) => {
            if(!bonus.frequency || !bonus.bonusType) {
                nextErrors[`bonus.${index}.type`] = "Type is required";
                return;
            }

            if(!hasValue(bonus.amount)) nextErrors[`bonus.${index}.amount`] = "Amount is required";
            if(bonus.frequency === PayrollBonusFrequency.Annually) {
                if(!hasValue(bonus.bonusMonth)) nextErrors[`bonus.${index}.month`] = "Month is required";
                if(!hasValue(bonus.bonusDay)) nextErrors[`bonus.${index}.day`] = "Day is required";
            } else if(bonus.frequency === PayrollBonusFrequency.Once && !bonus.onetimeBonusDate) {
                nextErrors[`bonus.${index}.date`] = "Effective Date is required";
            }
        });

        overtimes.forEach((overtime, index) => {
            if(!hasValue(overtime.hourlyCompensation)) nextErrors[`overtime.${index}.hourlyCompensation`] = "Hourly Rate is required";
            if(!hasValue(overtime.expectedHoursPerWeek)) nextErrors[`overtime.${index}.expectedHoursPerWeek`] = "Hours/Week is required";
            if(!overtime.effectiveStartDate) nextErrors[`overtime.${index}.startDate`] = "Start Date is required";
        });

        return nextErrors;
    }

    useEffect(() => {
        const nextErrors = validate();
        setErrors(nextErrors);
        onValidationChange?.(Object.keys(nextErrors).length > 0);
    }, [base, raises, bonuses, overtimes, onValidationChange]);

    function updateBase(updates: Partial<LocalBaseDefault>) {
        if(base) {
            setBases([{...base, ...updates}]);
        } else {
            setBases([{
                _localId: uuid(),
                compensationType: undefined,
                annualCompensation: "",
                hourlyCompensation: "",
                expectedHoursPerWeek: "",
                ...updates,
            }]);
        }
    }

    function updateRaise(index: number, updates: Partial<LocalRaiseDefault>) {
        const newRaises = [...raises];
        newRaises[index] = {...newRaises[index]!, ...updates};
        setRaises(newRaises);
    }

    function updateBonus(index: number, updates: Partial<LocalBonusDefault>) {
        const newBonuses = [...bonuses];
        newBonuses[index] = {...newBonuses[index]!, ...updates};
        setBonuses(newBonuses);
    }

    function updateOvertime(index: number, updates: Partial<LocalOvertimeDefault>) {
        const newOvertimes = [...overtimes];
        newOvertimes[index] = {...newOvertimes[index]!, ...updates};
        setOvertimes(newOvertimes);
    }

    return (
        <div className={styles.subForm}>
            {/* Base Compensation */}
            <div className={styles.section}>
                    <Row justifyContent="between">
                        <Col><h4 className={styles.sectionHeader}>Base Compensation</h4></Col>
                        {showResetControls && <Col textAlign="end"><Button isBasic disabled={!basesChanged} onClick={onResetBases}>Undo</Button></Col>}
                    </Row>
                    {base ? (
                        <>
                            <Row justifyContent="between">
                                <Col>
                                    <Label className={styles.subHeader}>Base Compensation</Label>
                                </Col>
                                <Button isBasic isDanger onClick={() => setBases([])}>Remove</Button>
                            </Row>
                            <Field className={styles.field}>
                                <Label className={styles.label}>Compensation Type</Label>
                                <CustomZDDropdown
                                    widthSpec={"100%"}
                                    applySelectedItems={(items) => {
                                        const val = Array.isArray(items) ? items[0] : items;
                                        updateBase({compensationType: val as PayrollCompensationType});
                                    }}
                                    initialSelectedItems={base.compensationType ? [base.compensationType] : [""]}
                                    openDropdownPlaceholder="Salary"
                                    closedDropdownPlaceholder=""
                                    options={AllPayrollCompensationType.map(opt => ({value: opt.value, label: opt.label}))}
                                    isError={!!fieldError("base.compensationType")}
                                    errorMessage={fieldError("base.compensationType")}
                                    onBlur={() => markTouched("base.compensationType")}
                                    noSearchIcon
                                />
                            </Field>
                            {base.compensationType === PayrollCompensationType.Annual ? (
                                <Row justifyContent="between">
                                    <Col>
                                        <Field className={styles.field}>
                                            <Label className={styles.label}>Annual Amount</Label>
                                            <Input placeholder="$" type="number" value={base.annualCompensation}
                                                   validation={fieldError("base.annualCompensation") ? "error" : undefined}
                                                   onBlur={() => markTouched("base.annualCompensation")}
                                                   onChange={(evt) => updateBase({annualCompensation: evt.target.value})}
                                            />
                                            {fieldError("base.annualCompensation") && <Message validation="error">{fieldError("base.annualCompensation")}</Message>}
                                        </Field>
                                    </Col>
                                </Row>
                            ) : base.compensationType === PayrollCompensationType.Hourly ? (
                                <Row justifyContent="between">
                                    <Col>
                                        <Field className={styles.field}>
                                            <Label className={styles.label}>Hourly Rate</Label>
                                            <Input placeholder="$" type="number" value={base.hourlyCompensation}
                                                   validation={fieldError("base.hourlyCompensation") ? "error" : undefined}
                                                   onBlur={() => markTouched("base.hourlyCompensation")}
                                                   onChange={(evt) => updateBase({hourlyCompensation: evt.target.value})}
                                            />
                                            {fieldError("base.hourlyCompensation") && <Message validation="error">{fieldError("base.hourlyCompensation")}</Message>}
                                        </Field>
                                    </Col>
                                    <Col>
                                        <Field className={styles.field}>
                                            <Label className={styles.label}>Hours/Week</Label>
                                            <Input placeholder="Hours" type="number" value={base.expectedHoursPerWeek}
                                                   validation={fieldError("base.expectedHoursPerWeek") ? "error" : undefined}
                                                   onBlur={() => markTouched("base.expectedHoursPerWeek")}
                                                   onChange={(evt) => updateBase({expectedHoursPerWeek: evt.target.value})}
                                            />
                                            {fieldError("base.expectedHoursPerWeek") && <Message validation="error">{fieldError("base.expectedHoursPerWeek")}</Message>}
                                        </Field>
                                    </Col>
                                </Row>
                            ) : null}
                        </>
                    ) : (
                        <Button isBasic className={styles.addButton} onClick={() => setBases([{
                            _localId: uuid(),
                            compensationType: undefined,
                            annualCompensation: "",
                            hourlyCompensation: "",
                            expectedHoursPerWeek: "",
                        }])}>Add</Button>
                    )}
            </div>

            {/* Raises */}
            <div className={styles.section}>
                    <Row justifyContent="between">
                        <Col><h4 className={styles.sectionHeader}>Raises</h4></Col>
                        {showResetControls && <Col textAlign="end"><Button isBasic disabled={!raisesChanged} onClick={onResetRaises}>Undo</Button></Col>}
                    </Row>
                    {raises.map((raise, index) => (
                        <div key={raise._localId}>
                            <Row justifyContent="between">
                                <Col>
                                    <Label className={styles.subHeader}>Raise {index + 1}</Label>
                                </Col>
                                <Button isBasic isDanger onClick={() => {
                                    setRaises(raises.filter((_, i) => i !== index));
                                }}>Remove</Button>
                            </Row>
                            <Row justifyContent="between">
                                <Col size={12}>
                                    <Field className={styles.field}>
                                        <Label className={styles.label}>Type</Label>
                                        <CustomZDDropdown
                                            widthSpec={"100%"}
                                            applySelectedItems={(items) => {
                                                const val = Array.isArray(items) ? items[0] : items;
                                                const tokens: string[] = JSON.parse(val as string);
                                                if(tokens.length === 2) {
                                                    updateRaise(index, {
                                                        frequency: tokens[0] as PayrollRaiseFrequency,
                                                        raiseType: tokens[1] as PayrollRaiseType,
                                                        amount: "",
                                                        raiseMonth: "",
                                                        raiseDay: "",
                                                        onetimeRaiseDate: undefined,
                                                    });
                                                }
                                            }}
                                            initialSelectedItems={(raise.frequency && raise.raiseType) ? [JSON.stringify([raise.frequency, raise.raiseType])] : [""]}
                                            openDropdownPlaceholder="Select Raise Type"
                                            closedDropdownPlaceholder=""
                                            options={AllPayrollRaiseFrequencyAndType.map(opt => ({value: opt.value, label: opt.label}))}
                                            isError={!!fieldError(`raise.${index}.type`)}
                                            errorMessage={fieldError(`raise.${index}.type`)}
                                            onBlur={() => markTouched(`raise.${index}.type`)}
                                            noSearchIcon
                                        />
                                    </Field>
                                </Col>
                            </Row>
                            {raise.frequency === PayrollRaiseFrequency.Annually ? (
                                <Row justifyContent="between">
                                    <Col>
                                        <Field className={styles.field}>
                                            <Label className={styles.label}>
                                                {raise.raiseType === PayrollRaiseType.PercentOfSalary ? "Raise %" : "Raise Amount"}
                                            </Label>
                                            <Input
                                                placeholder={raise.raiseType === PayrollRaiseType.FixedAmount ? "$" : "%"}
                                                value={raise.amount}
                                                type="number"
                                                validation={fieldError(`raise.${index}.amount`) ? "error" : undefined}
                                                onBlur={() => markTouched(`raise.${index}.amount`)}
                                                onChange={(evt) => updateRaise(index, {amount: evt.target.value})}
                                            />
                                            {fieldError(`raise.${index}.amount`) && <Message validation="error">{fieldError(`raise.${index}.amount`)}</Message>}
                                        </Field>
                                    </Col>
                                    <Col>
                                        <Field className={styles.field}>
                                            <Label className={styles.label}>Month</Label>
                                            <div style={{marginTop: "8px"}}>
                                                <CustomZDDropdown
                                                    widthSpec={"100%"}
                                                    applySelectedItems={(items) => {
                                                        const val = Array.isArray(items) ? items[0] : items;
                                                        updateRaise(index, {raiseMonth: val as string});
                                                    }}
                                                    initialSelectedItems={raise.raiseMonth ? [raise.raiseMonth.toString()] : [""]}
                                                    openDropdownPlaceholder="Month"
                                                    closedDropdownPlaceholder=""
                                                    options={AllMonths.map(opt => ({value: opt.value, label: opt.label}))}
                                                    isError={!!fieldError(`raise.${index}.month`)}
                                                    errorMessage={fieldError(`raise.${index}.month`)}
                                                    onBlur={() => markTouched(`raise.${index}.month`)}
                                                    noSearchIcon
                                                />
                                            </div>
                                        </Field>
                                    </Col>
                                    <Col>
                                        <Field className={styles.field}>
                                            <Label className={styles.label}>Day</Label>
                                            <div style={{marginTop: "8px"}}>
                                                <CustomZDDropdown
                                                    widthSpec={"100%"}
                                                    applySelectedItems={(items) => {
                                                        const val = Array.isArray(items) ? items[0] : items;
                                                        updateRaise(index, {raiseDay: val as string});
                                                    }}
                                                    initialSelectedItems={raise.raiseDay ? [raise.raiseDay.toString()] : [""]}
                                                    openDropdownPlaceholder="Day"
                                                    closedDropdownPlaceholder=""
                                                    options={getDatesForMonth(raise.raiseMonth).map(opt => ({value: opt.value, label: opt.label}))}
                                                    isError={!!fieldError(`raise.${index}.day`)}
                                                    errorMessage={fieldError(`raise.${index}.day`)}
                                                    onBlur={() => markTouched(`raise.${index}.day`)}
                                                    noSearchIcon
                                                />
                                            </div>
                                        </Field>
                                    </Col>
                                </Row>
                            ) : raise.frequency === PayrollRaiseFrequency.Once ? (
                                <Row justifyContent="between">
                                    <Col>
                                        <Field className={styles.field}>
                                            <Label className={styles.label}>
                                                {raise.raiseType === PayrollRaiseType.PercentOfSalary ? "Raise %" : "Raise Amount"}
                                            </Label>
                                            <Input
                                                placeholder={raise.raiseType === PayrollRaiseType.FixedAmount ? "$" : "%"}
                                                value={raise.amount}
                                                type="number"
                                                validation={fieldError(`raise.${index}.amount`) ? "error" : undefined}
                                                onBlur={() => markTouched(`raise.${index}.amount`)}
                                                onChange={(evt) => updateRaise(index, {amount: evt.target.value})}
                                            />
                                            {fieldError(`raise.${index}.amount`) && <Message validation="error">{fieldError(`raise.${index}.amount`)}</Message>}
                                        </Field>
                                    </Col>
                                    <Col>
                                        <Field className={styles.field}>
                                            <Label className={styles.label}>Effective Date</Label>
                                            <PayrollDatePicker
                                                dateValue={raise.onetimeRaiseDate ?? ""}
                                                onDateChange={(newDate) => {
                                                    updateRaise(index, {onetimeRaiseDate: ensureDateInUtc(newDate)?.toISOString()});
                                                }}
                                                onBlur={() => markTouched(`raise.${index}.date`)}
                                            />
                                            {fieldError(`raise.${index}.date`) && <Message validation="error">{fieldError(`raise.${index}.date`)}</Message>}
                                        </Field>
                                    </Col>
                                </Row>
                            ) : null}
                        </div>
                    ))}
                    <Button isBasic className={styles.addButton} onClick={() => {
                        setRaises([...raises, {
                            _localId: uuid(),
                            frequency: null,
                            raiseType: null,
                            amount: "",
                            raiseMonth: "",
                            raiseDay: "",
                            onetimeRaiseDate: undefined,
                        }]);
                    }}>Add</Button>
            </div>

            {/* Bonuses */}
            <div className={styles.section}>
                    <Row justifyContent="between">
                        <Col><h4 className={styles.sectionHeader}>Bonuses</h4></Col>
                        {showResetControls && <Col textAlign="end"><Button isBasic disabled={!bonusesChanged} onClick={onResetBonuses}>Undo</Button></Col>}
                    </Row>
                    {bonuses.map((bonus, index) => (
                        <div key={bonus._localId}>
                            <Row justifyContent="between">
                                <Col>
                                    <Label className={styles.subHeader}>Bonus {index + 1}</Label>
                                </Col>
                                <Button isBasic isDanger onClick={() => {
                                    setBonuses(bonuses.filter((_, i) => i !== index));
                                }}>Remove</Button>
                            </Row>
                            <Row justifyContent="between">
                                <Col size={12}>
                                    <Field className={styles.field}>
                                        <Label className={styles.label}>Type</Label>
                                        <CustomZDDropdown
                                            widthSpec={"100%"}
                                            applySelectedItems={(items) => {
                                                const val = Array.isArray(items) ? items[0] : items;
                                                const tokens: string[] = JSON.parse(val as string);
                                                if(tokens.length === 2) {
                                                    updateBonus(index, {
                                                        frequency: tokens[0] as PayrollBonusFrequency,
                                                        bonusType: tokens[1] as PayrollBonusType,
                                                        amount: "",
                                                        bonusMonth: "",
                                                        bonusDay: "",
                                                        onetimeBonusDate: undefined,
                                                    });
                                                }
                                            }}
                                            initialSelectedItems={(bonus.frequency && bonus.bonusType) ? [JSON.stringify([bonus.frequency, bonus.bonusType])] : [""]}
                                            openDropdownPlaceholder="Select Bonus Type"
                                            closedDropdownPlaceholder=""
                                            options={AllPayrollBonusFrequencyAndType.map(opt => ({value: opt.value, label: opt.label}))}
                                            isError={!!fieldError(`bonus.${index}.type`)}
                                            errorMessage={fieldError(`bonus.${index}.type`)}
                                            onBlur={() => markTouched(`bonus.${index}.type`)}
                                            noSearchIcon
                                        />
                                    </Field>
                                </Col>
                            </Row>
                            {bonus.frequency === PayrollBonusFrequency.Annually ? (
                                <Row justifyContent="between">
                                    <Col>
                                        <Field className={styles.field}>
                                            <Label className={styles.label}>
                                                {bonus.bonusType === PayrollBonusType.PercentOfSalary ? "Bonus %" : "Bonus Amount"}
                                            </Label>
                                            <Input
                                                placeholder={bonus.bonusType === PayrollBonusType.FixedAmount ? "$" : "%"}
                                                value={bonus.amount}
                                                type="number"
                                                validation={fieldError(`bonus.${index}.amount`) ? "error" : undefined}
                                                onBlur={() => markTouched(`bonus.${index}.amount`)}
                                                onChange={(evt) => updateBonus(index, {amount: evt.target.value})}
                                            />
                                            {fieldError(`bonus.${index}.amount`) && <Message validation="error">{fieldError(`bonus.${index}.amount`)}</Message>}
                                        </Field>
                                    </Col>
                                    <Col>
                                        <Field className={styles.field}>
                                            <Label className={styles.label}>Month</Label>
                                            <div style={{marginTop: "8px"}}>
                                                <CustomZDDropdown
                                                    widthSpec={"100%"}
                                                    applySelectedItems={(items) => {
                                                        const val = Array.isArray(items) ? items[0] : items;
                                                        updateBonus(index, {bonusMonth: val as string});
                                                    }}
                                                    initialSelectedItems={bonus.bonusMonth ? [bonus.bonusMonth.toString()] : [""]}
                                                    openDropdownPlaceholder="Month"
                                                    closedDropdownPlaceholder=""
                                                    options={AllMonths.map(opt => ({value: opt.value, label: opt.label}))}
                                                    isError={!!fieldError(`bonus.${index}.month`)}
                                                    errorMessage={fieldError(`bonus.${index}.month`)}
                                                    onBlur={() => markTouched(`bonus.${index}.month`)}
                                                    noSearchIcon
                                                />
                                            </div>
                                        </Field>
                                    </Col>
                                    <Col>
                                        <Field className={styles.field}>
                                            <Label className={styles.label}>Day</Label>
                                            <div style={{marginTop: "8px"}}>
                                                <CustomZDDropdown
                                                    widthSpec={"100%"}
                                                    applySelectedItems={(items) => {
                                                        const val = Array.isArray(items) ? items[0] : items;
                                                        updateBonus(index, {bonusDay: val as string});
                                                    }}
                                                    initialSelectedItems={bonus.bonusDay ? [bonus.bonusDay.toString()] : [""]}
                                                    openDropdownPlaceholder="Day"
                                                    closedDropdownPlaceholder=""
                                                    options={getDatesForMonth(bonus.bonusMonth).map(opt => ({value: opt.value, label: opt.label}))}
                                                    isError={!!fieldError(`bonus.${index}.day`)}
                                                    errorMessage={fieldError(`bonus.${index}.day`)}
                                                    onBlur={() => markTouched(`bonus.${index}.day`)}
                                                    noSearchIcon
                                                />
                                            </div>
                                        </Field>
                                    </Col>
                                </Row>
                            ) : bonus.frequency === PayrollBonusFrequency.Once ? (
                                <Row justifyContent="between">
                                    <Col>
                                        <Field className={styles.field}>
                                            <Label className={styles.label}>
                                                {bonus.bonusType === PayrollBonusType.PercentOfSalary ? "Bonus %" : "Bonus Amount"}
                                            </Label>
                                            <Input
                                                placeholder={bonus.bonusType === PayrollBonusType.FixedAmount ? "$" : "%"}
                                                value={bonus.amount}
                                                type="number"
                                                validation={fieldError(`bonus.${index}.amount`) ? "error" : undefined}
                                                onBlur={() => markTouched(`bonus.${index}.amount`)}
                                                onChange={(evt) => updateBonus(index, {amount: evt.target.value})}
                                            />
                                            {fieldError(`bonus.${index}.amount`) && <Message validation="error">{fieldError(`bonus.${index}.amount`)}</Message>}
                                        </Field>
                                    </Col>
                                    <Col>
                                        <Field className={styles.field}>
                                            <Label className={styles.label}>Effective Date</Label>
                                            <PayrollDatePicker
                                                dateValue={bonus.onetimeBonusDate ?? ""}
                                                onDateChange={(newDate) => {
                                                    updateBonus(index, {onetimeBonusDate: ensureDateInUtc(newDate)?.toISOString()});
                                                }}
                                                onBlur={() => markTouched(`bonus.${index}.date`)}
                                            />
                                            {fieldError(`bonus.${index}.date`) && <Message validation="error">{fieldError(`bonus.${index}.date`)}</Message>}
                                        </Field>
                                    </Col>
                                </Row>
                            ) : null}
                        </div>
                    ))}
                    <Button isBasic className={styles.addButton} onClick={() => {
                        setBonuses([...bonuses, {
                            _localId: uuid(),
                            frequency: null,
                            bonusType: null,
                            amount: "",
                            bonusMonth: "",
                            bonusDay: "",
                            onetimeBonusDate: undefined,
                        }]);
                    }}>Add</Button>
            </div>

            {/* Overtimes */}
            <div className={styles.section}>
                    <Row justifyContent="between">
                        <Col><h4 className={styles.sectionHeader}>Overtime</h4></Col>
                        {showResetControls && <Col textAlign="end"><Button isBasic disabled={!overtimesChanged} onClick={onResetOvertimes}>Undo</Button></Col>}
                    </Row>
                    {overtimes.map((overtime, index) => (
                        <div key={overtime._localId}>
                            <Row justifyContent="between">
                                <Col>
                                    <Label className={styles.subHeader}>Overtime {index + 1}</Label>
                                </Col>
                                <Button isBasic isDanger onClick={() => {
                                    setOvertimes(overtimes.filter((_, i) => i !== index));
                                }}>Remove</Button>
                            </Row>
                            <Row justifyContent="between">
                                <Col>
                                    <Field className={styles.field}>
                                        <Label className={styles.label}>Hourly Rate</Label>
                                        <Input placeholder="$" type="number" value={overtime.hourlyCompensation}
                                               validation={fieldError(`overtime.${index}.hourlyCompensation`) ? "error" : undefined}
                                               onBlur={() => markTouched(`overtime.${index}.hourlyCompensation`)}
                                               onChange={(evt) => updateOvertime(index, {hourlyCompensation: evt.target.value})}
                                        />
                                        {fieldError(`overtime.${index}.hourlyCompensation`) && <Message validation="error">{fieldError(`overtime.${index}.hourlyCompensation`)}</Message>}
                                    </Field>
                                </Col>
                                <Col>
                                    <Field className={styles.field}>
                                        <Label className={styles.label}>Hours/Week</Label>
                                        <Input placeholder="Hours" type="number" value={overtime.expectedHoursPerWeek}
                                               validation={fieldError(`overtime.${index}.expectedHoursPerWeek`) ? "error" : undefined}
                                               onBlur={() => markTouched(`overtime.${index}.expectedHoursPerWeek`)}
                                               onChange={(evt) => updateOvertime(index, {expectedHoursPerWeek: evt.target.value})}
                                        />
                                        {fieldError(`overtime.${index}.expectedHoursPerWeek`) && <Message validation="error">{fieldError(`overtime.${index}.expectedHoursPerWeek`)}</Message>}
                                    </Field>
                                </Col>
                            </Row>
                            <Row justifyContent="between">
                                <Col>
                                    <Field className={styles.field}>
                                        <Label className={styles.label}>Start Date</Label>
                                        <PayrollDatePicker
                                            dateValue={overtime.effectiveStartDate ?? ""}
                                            onDateChange={(newDate) => {
                                                updateOvertime(index, {effectiveStartDate: ensureDateInUtc(newDate)?.toISOString()});
                                            }}
                                            onBlur={() => markTouched(`overtime.${index}.startDate`)}
                                        />
                                        {fieldError(`overtime.${index}.startDate`) && <Message validation="error">{fieldError(`overtime.${index}.startDate`)}</Message>}
                                    </Field>
                                </Col>
                                <Col>
                                    <Field className={styles.field}>
                                        <Label className={styles.label}>End Date</Label>
                                        <PayrollDatePicker
                                            dateValue={overtime.effectiveEndDate ?? ""}
                                            onDateChange={(newDate) => {
                                                updateOvertime(index, {effectiveEndDate: ensureDateInUtc(newDate)?.toISOString()});
                                            }}
                                        />
                                    </Field>
                                </Col>
                            </Row>
                        </div>
                    ))}
                    <Button isBasic className={styles.addButton} onClick={() => {
                        setOvertimes([...overtimes, {
                            _localId: uuid(),
                            hourlyCompensation: "",
                            expectedHoursPerWeek: "",
                            effectiveStartDate: undefined,
                            effectiveEndDate: undefined,
                        }]);
                    }}>Add</Button>
            </div>
        </div>
    );
}
